import React, { createContext, useEffect, useState, useCallback } from 'react';
import { UserInfo } from '../types';
import { apiClient } from '../api/client';

interface AuthContextType {
  user: UserInfo | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
  token: string | null;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Federate OIDC configuration
const FEDERATE_ISSUER = import.meta.env.VITE_FEDERATE_ISSUER || 'https://idp.federate.amazon.com';
const FEDERATE_CLIENT_ID = import.meta.env.VITE_FEDERATE_CLIENT_ID || 'maintenance.cwl1.rme.amazon.dev';
const REDIRECT_URI = window.location.origin + '/auth/callback';
const API_BASE = import.meta.env.VITE_API_URL || '/api';
const AUTHORIZE_ENDPOINT = `${FEDERATE_ISSUER}/api/oauth2/v1/authorize`;

// PKCE helpers
function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('').slice(0, length);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const login = useCallback(async () => {
    // Generate PKCE code verifier and challenge
    const codeVerifier = generateRandomString(64);
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Generate state and nonce
    const state = crypto.randomUUID();
    const nonce = crypto.randomUUID();

    // Store for callback validation
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);
    sessionStorage.setItem('oauth_state', state);
    sessionStorage.setItem('oidc_nonce', nonce);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: FEDERATE_CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      scope: 'openid',
      nonce,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });
    window.location.href = `${AUTHORIZE_ENDPOINT}?${params}`;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oidc_nonce');
    sessionStorage.removeItem('pkce_code_verifier');
    window.location.href = '/';
  }, []);

  const exchangeCodeForToken = useCallback(async (code: string): Promise<string | null> => {
    const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
    sessionStorage.removeItem('pkce_code_verifier');

    if (!codeVerifier) {
      console.error('No code verifier found in session');
      return null;
    }

    try {
      const response = await fetch(`${API_BASE}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          redirect_uri: REDIRECT_URI,
          code_verifier: codeVerifier,
        }),
      });

      if (!response.ok) {
        const err = await response.text();
        console.error('Token exchange failed:', response.status, err);
        return null;
      }

      const data = await response.json();
      return data.id_token || null;
    } catch (err) {
      console.error('Token exchange error:', err);
      return null;
    }
  }, []);

  const fetchUserInfo = useCallback(async (idToken: string) => {
    try {
      const response = await apiClient.get('/me', idToken);
      setUser(response);
      setToken(idToken);
      sessionStorage.setItem('id_token', idToken);
    } catch (err) {
      console.error('Failed to fetch user info:', err);
      sessionStorage.removeItem('id_token');
      setToken(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check for authorization code in URL query params (PKCE callback)
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const returnedState = urlParams.get('state');

    if (code) {
      // Validate state parameter (CSRF protection)
      const savedState = sessionStorage.getItem('oauth_state');
      sessionStorage.removeItem('oauth_state');

      if (!returnedState || returnedState !== savedState) {
        console.error('OAuth state mismatch - possible CSRF attack');
        setIsLoading(false);
        return;
      }

      // Exchange code for token
      window.history.replaceState({}, '', '/');
      exchangeCodeForToken(code).then((idToken) => {
        if (idToken) {
          // Validate nonce
          try {
            const payload = JSON.parse(atob(idToken.split('.')[1]));
            const expectedNonce = sessionStorage.getItem('oidc_nonce');
            sessionStorage.removeItem('oidc_nonce');

            if (!expectedNonce || payload.nonce !== expectedNonce) {
              console.error('Nonce mismatch - possible replay attack');
              setIsLoading(false);
              return;
            }
          } catch (e) {
            console.error('Failed to decode id_token for nonce validation:', e);
            setIsLoading(false);
            return;
          }
          fetchUserInfo(idToken);
        } else {
          setIsLoading(false);
        }
      });
      return;
    }

    // Check for existing token in session storage
    const existingToken = sessionStorage.getItem('id_token');
    if (existingToken) {
      fetchUserInfo(existingToken);
    } else {
      setIsLoading(false);
    }
  }, [fetchUserInfo, exchangeCodeForToken]);

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated: !!user,
      login,
      logout,
      token,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
